import { defineStore } from 'pinia';
import moment from 'moment';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type {
    AntennaUsageStateType,
    AntennaUsageGettersType,
    AntennaUsageActionsType,
} from '@/types/stores/AntennaUsageStoreTypes';
import type { AntennaType } from '@/types/AntennaType';
import type { AntennaUsageFilterType, AntennaUsageDataType } from '@/types/statistics/AntennaUsageType';
import type { FilterChipType } from '@/types/statistics/FilterChipType';
import {
    formatDateRangeChip,
    createAntennaFilterChip,
    createLocationFilterChip,
    buildStatisticsQueryParams,
} from '@/utils/storeUtils';
import { getIntelligentChartColors } from '@/utils/chartColorUtils';
import { buildQueryString } from '@/utils/queryUtils';
import i18n from '@/plugins/i18n';
import { VDataTableServer } from 'vuetify/components';
import type { LineChartDataType } from '@/types/ChartType';
import type { AxiosResponse } from 'axios';

const createDefaultFilters = (): AntennaUsageFilterType => ({
    dateRange: {
        start: moment().subtract(7, 'days').startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
    },
    groupedBy: 'day',
    antennaUuid: undefined,
    locationId: undefined,
});

const createDefaultData = (): AntennaUsageDataType => ({
    groupedBy: 'day',
    startDate: '',
    endDate: '',
    antennaUuid: undefined,
    locationId: undefined,
    summary: {
        totalReads: 0,
        averagePerHour: 0,
        mostActive: null,
    },
    groups: [],
});

const createDefaultHeaders = (): VDataTableServer['$props']['headers'] => [
    {
        title: 'statistics.antenna.table.period',
        key: 'type',
        sortable: true,
    },
    {
        title: 'statistics.antenna.table.totalReads',
        key: 'total_reads',
        sortable: true,
        align: 'end',
    },
    {
        title: 'statistics.antenna.table.averageReadsPerTag',
        key: 'average_reads_per_tag',
        sortable: true,
        align: 'end',
    },
];

export const useAntennaUsageStore = defineStore<
    'antennaUsage',
    AntennaUsageStateType,
    AntennaUsageGettersType,
    AntennaUsageActionsType
>('antennaUsage', {
    state: (): AntennaUsageStateType => ({
        isLoading: false,
        filters: createDefaultFilters(),
        data: createDefaultData(),
        headers: createDefaultHeaders(),
        antennas: [],
    }),

    getters: {
        getFilteredData: state => state.data || createDefaultData(),
        getTotalReads: state => state.data?.summary?.totalReads || 0,
        hasData: state => (state.data?.groups?.length || 0) > 0,
        getFilterChips: (state): FilterChipType[] => {
            const chips: FilterChipType[] = [];
            const { t } = i18n.global;

            chips.push({
                key: 'groupedBy',
                label: `${t('common.groupedBy')}: ${t(`common.grouping.${state.filters.groupedBy}`)}`,
                value: state.filters.groupedBy,
                icon: 'mdi-group',
                removable: false,
            });

            if (state.filters.dateRange?.start && state.filters.dateRange?.end) {
                chips.push(formatDateRangeChip(state.filters.dateRange));
            }

            const antennaChip = createAntennaFilterChip(state.filters.antennaUuid, state.data?.groups || []);
            if (antennaChip) {
                chips.push(antennaChip);
            }

            const locationChip = createLocationFilterChip(state.filters.locationId, state.data?.groups || []);
            if (locationChip) {
                chips.push(locationChip);
            }

            return chips;
        },
        getTranslatedHeaders: state => {
            const { t } = i18n.global;

            return (
                state.headers?.map(header => ({
                    ...header,
                    title: header.title ? t(header.title) : header.title,
                })) || []
            );
        },
        getAntennaOptions: (state: AntennaUsageStateType) => {
            if (!state.antennas.length) {
                return [];
            }

            return state.antennas.map(antenna => ({
                title: antenna.reader_name ? `${antenna.reader_name} — ${antenna.name}` : antenna.name,
                value: antenna.uuid,
            }));
        },

        getChartData:
            state =>
                (metric: 'total_reads' | 'average_reads_per_tag'): LineChartDataType => {
                    if (!state.data?.groups?.length) {
                        return { labels: [], datasets: [] };
                    }

                    const labels = state.data.groups.map(group => group.label);

                    const antennaMap = new Map<string, { name: string; data: number[] }>();

                    for (const group of state.data.groups) {
                        for (const antenna of group.data) {
                            if (!antennaMap.has(antenna.antenna_uuid)) {
                                antennaMap.set(antenna.antenna_uuid, {
                                    name: antenna.antenna_name,
                                    data: [],
                                });
                            }
                            const antennaData = antennaMap.get(antenna.antenna_uuid)!;
                            antennaData.data.push(antenna[metric]);
                        }
                    }

                    const antennaEntries = Array.from(antennaMap.entries());
                    const colorPairs = getIntelligentChartColors(antennaEntries.length);

                    const datasets = antennaEntries.map(([_uuid, antennaInfo], index) => {
                        const colorPair = colorPairs[index];

                        return {
                            label: antennaInfo.name,
                            data: antennaInfo.data,
                            borderColor: colorPair.borderColor,
                            backgroundColor: colorPair.backgroundColor,
                            fill: false,
                            tension: 0.1,
                        };
                    });

                    return { labels, datasets };
                },
    },

    actions: {
        async fetchAntennas() {
            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: '/tenant/antennas',
                onSuccess: (response: AxiosResponse<AntennaType[]>) => {
                    this.antennas = response.data;
                },
            });
        },

        async fetchData() {
            this.isLoading = true;
            const queryParams = buildStatisticsQueryParams(this.filters);

            if (this.filters.antennaUuid && this.filters.antennaUuid.length > 0) {
                queryParams.antennaUuids = this.filters.antennaUuid;
            }

            if (this.filters.locationId && this.filters.locationId.length > 0) {
                queryParams.locationIds = this.filters.locationId;
            }
            const query = buildQueryString(queryParams);

            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint: `/logs/statistics/antenna/daily-stats?${query}`,
                onSuccess: (response: AxiosResponse<AntennaUsageDataType>) => {
                    this.data = response.data;
                },
            });
            this.isLoading = false;
        },
        resetFilters() {
            this.filters = createDefaultFilters();
        },

        async removeFilter(filterKey: string) {
            const defaultFilters = createDefaultFilters();

            switch (filterKey) {
            case 'dateRange':
                this.filters.dateRange = defaultFilters.dateRange;
                break;
            case 'groupedBy':
                this.filters.groupedBy = defaultFilters.groupedBy;
                break;
            case 'antennaUuid':
                this.filters.antennaUuid = undefined;
                break;
            case 'locationId':
                this.filters.locationId = undefined;
                break;
            default:
                console.warn(`Unknown filter key: ${filterKey}`);

                return;
            }

            await this.fetchData();
        },
    },
});
